import React, { useEffect, useRef, useState } from 'react';
import { message } from '../utils/antdMessage';
import { fromMapLngLat } from '../utils/dashboard/basemapCoord';
import {
  ORTHO_BBOX_SOURCE,
  ORTHO_BBOX_FILL,
  ORTHO_BBOX_LINE,
  ORTHO_BBOX_HANDLES,
  ORTHO_BBOX_HANDLES_LAYER,
  normalizeDisplayBbox,
  displayBboxPolygon,
  displayBboxHandles,
  applyHandleResize,
  cursorForHandle,
  applyOrthoBboxCursor,
  clearOrthoBboxCursor
} from '../utils/orthoToolbox';

/**
 * 地图框选引擎：画框/调边角/确认/取消与地图事件绑定
 */
export default function useOrthoBboxSelect({
  getMap,
  mapReady,
  onStartInteract,
  drawingToolsActiveRef,
  setExpanded,
  setBusy,
  canManage = false,
  setOrthoManageOpen,
  ensureMapVisibleForSelect,
  prepareFlatMapForPhotoFly,
  flyToPhotoRenderArea,
  onConfirm,
  /** 手机端收起侧栏/队员栏；桌面端继续展开工具箱 */
  onSelectChrome,
  isMobile = false,
  /** 与原组件依赖数组对齐（effect 内未直接使用） */
  activeId
}) {
  const [selectMode, setSelectMode] = useState(null); // render-aoi | map-road | map-contour | map-poi | map-building | ortho-2d | ortho-3d | null
  /** drawing=拖拽画框；editing=松手后可调边角，待确认 */
  const [bboxPhase, setBboxPhase] = useState(null); // drawing | editing | null
  const startLngLatRef = useRef(null);
  const boxMarkerRef = useRef(null);
  /** 草稿选框（显示坐标，未确认） */
  const draftBboxRef = useRef(null);
  const bboxPhaseRef = useRef(null);
  /** 编辑拖拽：边角/平移 */
  const editDragRef = useRef(null);
  /** 仅左键按下后的拖拽才算画框，避免滚轮/触控板抬起误结束 */
  const leftDragActiveRef = useRef(false);
  const cancelBboxSelectRef = useRef(() => {});
  /** 与 selectMode 同步，供框选 cleanup 判断是否仍在框选（避免重置 mode 时误关屏蔽） */
  const selectModeRef = useRef(null);
  /** 密度预览→弹窗→框选整段会话；防止 cleanup/点穿地图抢飞到设备 */
  const aoiInteractRef = useRef(false);

  useEffect(() => {
    selectModeRef.current = selectMode;
  }, [selectMode]);

  useEffect(() => {
    bboxPhaseRef.current = bboxPhase;
  }, [bboxPhase]);

  /** 框选/密度预览期间禁止「地球空白点击→飞设备」抢镜头 */
  const setBlockEarthBlankFly = (blocked) => {
    const on = !!blocked;
    const map = getMap?.();
    if (map) {
      try {
        map.__zhxtBlockEarthBlankFly = on;
      } catch (_) { /* ignore */ }
    }
    // 与画板同一开关，地球空白点击逻辑两边都能拦
    if (drawingToolsActiveRef) {
      drawingToolsActiveRef.current = on;
    }
  };

  const beginAoiInteract = () => {
    aoiInteractRef.current = true;
    setBlockEarthBlankFly(true);
  };

  const endAoiInteractSoon = (ms = 700) => {
    aoiInteractRef.current = false;
    setTimeout(() => {
      if (!selectModeRef.current && !aoiInteractRef.current) {
        setBlockEarthBlankFly(false);
      }
    }, ms);
  };

  const clearBoxPreview = () => {
    const map = getMap?.();
    if (!map) {
      boxMarkerRef.current = null;
      draftBboxRef.current = null;
      return;
    }
    try {
      if (map.getLayer(ORTHO_BBOX_HANDLES_LAYER)) map.removeLayer(ORTHO_BBOX_HANDLES_LAYER);
      if (map.getLayer(ORTHO_BBOX_FILL)) map.removeLayer(ORTHO_BBOX_FILL);
      if (map.getLayer(ORTHO_BBOX_LINE)) map.removeLayer(ORTHO_BBOX_LINE);
      if (map.getSource(ORTHO_BBOX_HANDLES)) map.removeSource(ORTHO_BBOX_HANDLES);
      if (map.getSource(ORTHO_BBOX_SOURCE)) map.removeSource(ORTHO_BBOX_SOURCE);
    } catch (_) { /* ignore */ }
    boxMarkerRef.current = null;
    draftBboxRef.current = null;
    editDragRef.current = null;
    startLngLatRef.current = null;
  };

  const paintDraftBbox = (map, bbox, { showHandles = false } = {}) => {
    if (!map || !bbox) return;
    const poly = displayBboxPolygon(bbox);
    try {
      if (!map.getSource(ORTHO_BBOX_SOURCE)) {
        map.addSource(ORTHO_BBOX_SOURCE, { type: 'geojson', data: poly });
        map.addLayer({
          id: ORTHO_BBOX_FILL,
          type: 'fill',
          source: ORTHO_BBOX_SOURCE,
          paint: { 'fill-color': '#2563eb', 'fill-opacity': 0.22 }
        });
        map.addLayer({
          id: ORTHO_BBOX_LINE,
          type: 'line',
          source: ORTHO_BBOX_SOURCE,
          paint: { 'line-color': '#1d4ed8', 'line-width': 2.5 }
        });
      } else {
        map.getSource(ORTHO_BBOX_SOURCE).setData(poly);
      }

      if (showHandles) {
        const handles = displayBboxHandles(bbox);
        if (!map.getSource(ORTHO_BBOX_HANDLES)) {
          map.addSource(ORTHO_BBOX_HANDLES, { type: 'geojson', data: handles });
          map.addLayer({
            id: ORTHO_BBOX_HANDLES_LAYER,
            type: 'circle',
            source: ORTHO_BBOX_HANDLES,
            paint: {
              'circle-radius': 6,
              'circle-color': '#fff',
              'circle-stroke-width': 2,
              'circle-stroke-color': '#1d4ed8'
            }
          });
        } else {
          map.getSource(ORTHO_BBOX_HANDLES).setData(handles);
        }
      } else if (map.getLayer(ORTHO_BBOX_HANDLES_LAYER)) {
        map.removeLayer(ORTHO_BBOX_HANDLES_LAYER);
        if (map.getSource(ORTHO_BBOX_HANDLES)) map.removeSource(ORTHO_BBOX_HANDLES);
      }
      boxMarkerRef.current = true;
      draftBboxRef.current = bbox;
    } catch (err) {
      console.warn('[ortho] paintDraftBbox', err);
    }
  };

  const cancelBboxSelect = (opts = {}) => {
    const { silent = false } = opts;
    const mode = selectModeRef.current;
    setSelectMode(null);
    setBboxPhase(null);
    leftDragActiveRef.current = false;
    clearBoxPreview();
    const map = getMap?.();
    clearOrthoBboxCursor(map);
    try {
      map?.dragPan?.enable?.();
      map?.scrollZoom?.enable?.();
      map?.boxZoom?.enable?.();
    } catch (_) { /* ignore */ }
    if (mode === 'render-aoi') endAoiInteractSoon(400);
    else endAoiInteractSoon(200);
    if (!silent) message.info('已取消框选（右键也可取消）');
  };
  cancelBboxSelectRef.current = cancelBboxSelect;

  const redrawBboxSelect = () => {
    const map = getMap?.();
    clearBoxPreview();
    leftDragActiveRef.current = false;
    setBboxPhase('drawing');
    try {
      map?.dragPan?.disable?.();
      map?.scrollZoom?.enable?.();
      applyOrthoBboxCursor(map, 'drawing');
    } catch (_) { /* ignore */ }
    message.info('请重新拖拽画框；滚轮可缩放；右键取消');
  };

  const draftBboxToWgs84 = (map, bbox) => {
    const sw = fromMapLngLat(bbox.west, bbox.south, undefined, map) || [bbox.west, bbox.south];
    const ne = fromMapLngLat(bbox.east, bbox.north, undefined, map) || [bbox.east, bbox.north];
    const west = Math.min(sw[0], ne[0]);
    const east = Math.max(sw[0], ne[0]);
    const south = Math.min(sw[1], ne[1]);
    const north = Math.max(sw[1], ne[1]);
    if (!(west < east && south < north)) return null;
    return { west, south, east, north };
  };

  const confirmDraftBbox = async () => {
    const map = getMap?.();
    const mode = selectModeRef.current;
    const draft = draftBboxRef.current;
    if (!map || !mode || !draft) {
      message.warning('请先拖拽画出选框');
      return;
    }
    const wgs = draftBboxToWgs84(map, draft);
    if (!wgs) {
      message.warning('选框无效，请调整后再确认');
      return;
    }
    setBusy(true);
    try {
      await onConfirm?.({ mode, wgs, draft });
      setSelectMode(null);
      setBboxPhase(null);
      leftDragActiveRef.current = false;
      clearBoxPreview();
      clearOrthoBboxCursor(map);
      if (mode === 'render-aoi') {
        endAoiInteractSoon(800);
        // 框选完成后回到管理弹窗继续申请建模
        if (canManage) setOrthoManageOpen(true);
      } else endAoiInteractSoon(400);
    } catch (err) {
      message.error(err.message || '确认选区失败');
    } finally {
      setBusy(false);
      try {
        map.dragPan?.enable?.();
        map.scrollZoom?.enable?.();
      } catch (_) { /* ignore */ }
    }
  };

  const beginBboxSelect = (mode) => {
    const map = ensureMapVisibleForSelect() || getMap?.();
    if (!map || !mapReady) {
      message.warning('地图未就绪，请等底图加载后再框选；若仍空白请 Ctrl+F5 硬刷新');
      return;
    }
    if (!map.isStyleLoaded?.()) {
      message.warning('地图样式加载中，请稍候再框选');
      return;
    }
    if (mode === 'render-aoi') beginAoiInteract();
    else setBlockEarthBlankFly(true);
    onStartInteract?.();
    prepareFlatMapForPhotoFly(map);
    startLngLatRef.current = null;
    editDragRef.current = null;
    clearBoxPreview();
    onSelectChrome?.({ phase: 'begin' });
    if (!onSelectChrome) setExpanded(true);
    setBboxPhase('drawing');
    leftDragActiveRef.current = false;
    try {
      // 画框时禁平移；滚轮缩放始终可用，且不退出框选
      map.dragPan?.disable?.();
      map.scrollZoom?.enable?.();
      map.boxZoom?.disable?.();
      applyOrthoBboxCursor(map, 'drawing');
    } catch (_) { /* ignore */ }

    if (mode === 'render-aoi') {
      const flew = flyToPhotoRenderArea(map, { duration: 1500, maxZoom: 16 });
      if (!flew) {
        message.warning('未定位到航点范围，请在地图上自行框选；或取消后重点「再框选」');
      } else {
        message.info('已飞到拍摄主区域：十字光标拖拽画框；滚轮缩放不退出；右键取消');
      }
      setSelectMode(null);
      requestAnimationFrame(() => {
        setSelectMode('render-aoi');
        setBboxPhase('drawing');
        applyOrthoBboxCursor(map, 'drawing');
      });
      return;
    }

    setSelectMode(mode);
    setBboxPhase('drawing');
    applyOrthoBboxCursor(map, 'drawing');
    message.info(isMobile
      ? '用手指拖动画框；完成后点顶部「确认选区」'
      : '十字光标拖拽画选框；滚轮可缩放；右键取消');
  };

  useEffect(() => {
    const map = getMap?.();
    if (!map || !selectMode) return undefined;

    setBlockEarthBlankFly(true);
    const canvas = map.getCanvas();
    try {
      map.scrollZoom?.enable?.();
      map.boxZoom?.disable?.();
    } catch (_) { /* ignore */ }

    const screenDist = (a, b) => {
      try {
        const pa = map.project(a);
        const pb = map.project(b);
        return Math.hypot(pa.x - pb.x, pa.y - pb.y);
      } catch (_) {
        return 0;
      }
    };

    const hitHandle = (point) => {
      try {
        if (!map.getLayer(ORTHO_BBOX_HANDLES_LAYER)) return null;
        const feats = map.queryRenderedFeatures(point, { layers: [ORTHO_BBOX_HANDLES_LAYER] });
        return feats?.[0]?.properties?.id || null;
      } catch (_) {
        return null;
      }
    };

    const pointInDraft = (lngLat) => {
      const b = draftBboxRef.current;
      if (!b) return false;
      return lngLat.lng >= b.west && lngLat.lng <= b.east
        && lngLat.lat >= b.south && lngLat.lat <= b.north;
    };

    const setHandleCursor = (kind) => {
      const cur = cursorForHandle(kind);
      try {
        canvas.style.setProperty('cursor', cur, 'important');
        map.getCanvasContainer?.()?.style?.setProperty?.('cursor', cur, 'important');
      } catch (_) { /* ignore */ }
    };

    const syncCursor = (e) => {
      const phase = bboxPhaseRef.current;
      if (phase === 'drawing') {
        applyOrthoBboxCursor(map, 'drawing');
        return;
      }
      if (phase === 'editing') {
        if (editDragRef.current) {
          setHandleCursor(editDragRef.current.kind);
          return;
        }
        const hid = hitHandle(e.point);
        if (hid) {
          setHandleCursor(hid);
          return;
        }
        if (pointInDraft(e.lngLat)) {
          setHandleCursor('move');
          return;
        }
        applyOrthoBboxCursor(map, 'editing');
      }
    };

    /** 缩放后仍保持框选激活 + 十字光标（绝不因滚轮退出） */
    const keepSelectAliveAfterZoom = () => {
      if (!selectModeRef.current) return;
      try { map.scrollZoom?.enable?.(); } catch (_) { /* ignore */ }
      const phase = bboxPhaseRef.current || 'drawing';
      if (phase === 'drawing') {
        try { map.dragPan?.disable?.(); } catch (_) { /* ignore */ }
        applyOrthoBboxCursor(map, 'drawing');
      } else if (phase === 'editing') {
        applyOrthoBboxCursor(map, 'editing');
        if (draftBboxRef.current) {
          paintDraftBbox(map, draftBboxRef.current, { showHandles: true });
        }
      }
    };

    const onDown = (e) => {
      const phase = bboxPhaseRef.current;
      const btn = e.originalEvent?.button;
      // 右键：解除框选
      if (btn === 2) {
        e.preventDefault();
        try { e.originalEvent?.preventDefault?.(); } catch (_) { /* ignore */ }
        cancelBboxSelectRef.current?.();
        return;
      }
      // 仅左键画框；滚轮键/其它键忽略
      if (btn != null && btn !== 0) return;

      if (phase === 'drawing') {
        if (leftDragActiveRef.current) return;
        e.preventDefault();
        try {
          e.originalEvent?.stopPropagation?.();
          map.dragPan?.disable?.();
        } catch (_) { /* ignore */ }
        leftDragActiveRef.current = true;
        startLngLatRef.current = e.lngLat;
        applyOrthoBboxCursor(map, 'drawing');
        return;
      }

      if (phase === 'editing') {
        const hid = hitHandle(e.point);
        const b = draftBboxRef.current;
        if (!b) return;
        if (hid || pointInDraft(e.lngLat)) {
          e.preventDefault();
          try {
            e.originalEvent?.stopPropagation?.();
            map.dragPan?.disable?.();
          } catch (_) { /* ignore */ }
          leftDragActiveRef.current = true;
          editDragRef.current = {
            kind: hid || 'move',
            start: { lng: e.lngLat.lng, lat: e.lngLat.lat },
            origin: { ...b }
          };
          setHandleCursor(hid || 'move');
        }
      }
    };

    const onMove = (e) => {
      const phase = bboxPhaseRef.current;
      if (phase === 'drawing' && leftDragActiveRef.current && startLngLatRef.current) {
        const box = normalizeDisplayBbox(startLngLatRef.current, e.lngLat);
        paintDraftBbox(map, box, { showHandles: false });
        applyOrthoBboxCursor(map, 'drawing');
        return;
      }
      if (phase === 'editing' && editDragRef.current) {
        const drag = editDragRef.current;
        let next;
        if (drag.kind === 'move') {
          const dLng = e.lngLat.lng - drag.start.lng;
          const dLat = e.lngLat.lat - drag.start.lat;
          next = {
            west: drag.origin.west + dLng,
            east: drag.origin.east + dLng,
            south: drag.origin.south + dLat,
            north: drag.origin.north + dLat
          };
        } else {
          next = applyHandleResize(drag.origin, drag.kind, e.lngLat.lng, e.lngLat.lat);
        }
        paintDraftBbox(map, next, { showHandles: true });
        setHandleCursor(drag.kind);
        return;
      }
      syncCursor(e);
    };

    const onUp = (e) => {
      if (!leftDragActiveRef.current && !editDragRef.current && !startLngLatRef.current) return;
      const phase = bboxPhaseRef.current;
      if (phase === 'drawing' && startLngLatRef.current && leftDragActiveRef.current) {
        const a = startLngLatRef.current;
        const b = e.lngLat;
        startLngLatRef.current = null;
        leftDragActiveRef.current = false;
        // 过小（含误触）忽略，保持 drawing；缩放后仍可继续画
        if (screenDist(a, b) < 16) {
          clearBoxPreview();
          try { map.dragPan?.disable?.(); } catch (_) { /* ignore */ }
          applyOrthoBboxCursor(map, 'drawing');
          return;
        }
        const box = normalizeDisplayBbox(a, b);
        paintDraftBbox(map, box, { showHandles: true });
        setBboxPhase('editing');
        try { map.dragPan?.enable?.(); } catch (_) { /* ignore */ }
        applyOrthoBboxCursor(map, 'editing');
        message.info(isMobile
          ? '可拖白点调大小，点顶部「确认选区」生效'
          : '可拖边角调整；滚轮缩放不退出；确认后生效；右键取消');
        return;
      }

      if (phase === 'editing' && editDragRef.current) {
        editDragRef.current = null;
        leftDragActiveRef.current = false;
        try { map.dragPan?.enable?.(); } catch (_) { /* ignore */ }
        syncCursor(e);
      } else {
        leftDragActiveRef.current = false;
      }
    };

    // 仅在左键拖拽激活时处理抬起，避免滚轮/触控板缩放的 pointerup 误伤框选
    const onWinPointerUp = (ev) => {
      if (!leftDragActiveRef.current) return;
      if (ev.button != null && ev.button !== 0) return;
      try {
        const rect = canvas.getBoundingClientRect();
        const x = ev.clientX - rect.left;
        const y = ev.clientY - rect.top;
        const lngLat = map.unproject([x, y]);
        onUp({ lngLat, point: { x, y }, originalEvent: ev });
      } catch (_) {
        startLngLatRef.current = null;
        editDragRef.current = null;
        leftDragActiveRef.current = false;
        try { map.dragPan?.enable?.(); } catch (__) { /* ignore */ }
        keepSelectAliveAfterZoom();
      }
    };

    const onContextMenu = (e) => {
      e.preventDefault();
      try { e.originalEvent?.preventDefault?.(); } catch (_) { /* ignore */ }
      cancelBboxSelectRef.current?.();
    };

    const onCanvasContextMenu = (ev) => {
      ev.preventDefault();
      cancelBboxSelectRef.current?.();
    };

    if (bboxPhaseRef.current === 'drawing') {
      applyOrthoBboxCursor(map, 'drawing');
      try { map.dragPan?.disable?.(); } catch (_) { /* ignore */ }
    } else if (bboxPhaseRef.current === 'editing') {
      applyOrthoBboxCursor(map, 'editing');
      try { map.dragPan?.enable?.(); } catch (_) { /* ignore */ }
      if (draftBboxRef.current) {
        paintDraftBbox(map, draftBboxRef.current, { showHandles: true });
      }
    }

    map.on('mousedown', onDown);
    map.on('mousemove', onMove);
    map.on('mouseup', onUp);
    map.on('touchstart', onDown);
    map.on('touchmove', onMove);
    map.on('touchend', onUp);
    map.on('contextmenu', onContextMenu);
    map.on('wheel', keepSelectAliveAfterZoom);
    map.on('zoom', keepSelectAliveAfterZoom);
    map.on('zoomend', keepSelectAliveAfterZoom);
    canvas.addEventListener('contextmenu', onCanvasContextMenu);
    window.addEventListener('pointerup', onWinPointerUp);
    return () => {
      map.off('mousedown', onDown);
      map.off('mousemove', onMove);
      map.off('mouseup', onUp);
      map.off('touchstart', onDown);
      map.off('touchmove', onMove);
      map.off('touchend', onUp);
      map.off('contextmenu', onContextMenu);
      map.off('wheel', keepSelectAliveAfterZoom);
      map.off('zoom', keepSelectAliveAfterZoom);
      map.off('zoomend', keepSelectAliveAfterZoom);
      canvas.removeEventListener('contextmenu', onCanvasContextMenu);
      window.removeEventListener('pointerup', onWinPointerUp);
      leftDragActiveRef.current = false;
      try { map.dragPan?.enable?.(); } catch (_) { /* ignore */ }
      if (!selectModeRef.current) {
        clearOrthoBboxCursor(map);
      }
      setTimeout(() => {
        if (!selectModeRef.current && !aoiInteractRef.current) {
          setBlockEarthBlankFly(false);
        }
      }, 500);
    };
  }, [selectMode, getMap, mapReady, activeId]);

  // 进入 editing 后补一遍手柄（避免仅改 phase 时 effect 未重绑）
  useEffect(() => {
    const map = getMap?.();
    if (!map || !selectMode || bboxPhase !== 'editing') return;
    if (draftBboxRef.current) {
      paintDraftBbox(map, draftBboxRef.current, { showHandles: true });
    }
    try {
      map.dragPan?.enable?.();
      map.scrollZoom?.enable?.();
      applyOrthoBboxCursor(map, 'editing');
    } catch (_) { /* ignore */ }
  }, [bboxPhase, selectMode, getMap]);

  return {
    selectMode,
    setSelectMode,
    bboxPhase,
    setBboxPhase,
    selectModeRef,
    aoiInteractRef,
    clearBoxPreview,
    paintDraftBbox,
    cancelBboxSelect,
    redrawBboxSelect,
    confirmDraftBbox,
    beginBboxSelect,
    setBlockEarthBlankFly,
    beginAoiInteract,
    endAoiInteractSoon
  };
}
